'use client'

import React, { useState, useEffect } from 'react'
import { isWithinInterval } from 'date-fns'
import {
  WiDaySunny,
  WiNightClear,
  WiDayCloudy,
  WiNightCloudy,
  WiNightCloudyHigh,
  WiDayCloudyHigh,
  WiNightAltPartlyCloudy,
  WiDayFog,
  WiNightFog,
  WiDayRain,
  WiNightRain,
  WiRain,
  WiSnow,
  WiSleet,
  WiThunderstorm,
  WiStormShowers,
  WiSnowflakeCold,
  WiDaySnow,
  WiNightSnow,
  WiDaySnowWind,
  WiNightSnowWind,
  WiNightAltSnow,
  WiDaySnowThunderstorm,
  WiNightSnowThunderstorm
} from 'weather-icons-react'
import { cn } from '@/lib/utils'
import { EMPTY_WIDGET_SURFACE_CLASS } from './widget-surface'

interface WeatherWidgetProps {
  city?: string
  /** When true, use browser geolocation instead of city */
  useCurrentLocation?: boolean
  className?: string
  onClick?: () => void
}

interface WeatherData {
  temp: number
  code: number
  timezone: string
  currentTime: string
  sunrise: string
  sunset: string
}

async function fetchWeather(
  city?: string,
  useCurrentLocation?: boolean
): Promise<WeatherData | null> {
  let lat = 43.65
  let lon = -79.38
  if (useCurrentLocation && typeof navigator !== 'undefined' && navigator.geolocation) {
    try {
      const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 10000 })
      })
      lat = pos.coords.latitude
      lon = pos.coords.longitude
    } catch {
      // fall back to city or default
    }
  }
  if (!useCurrentLocation && city?.trim()) {
    try {
      const geoRes = await fetch(
        `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(city.trim())}&count=1&language=en&format=json`
      )
      const geo = await geoRes.json()
      const first = geo.results?.[0]
      if (first?.latitude != null && first?.longitude != null) {
        lat = first.latitude
        lon = first.longitude
      }
    } catch {
      // use default
    }
  }
  try {
    const res = await fetch(
      `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,weather_code&daily=sunrise,sunset&timezone=auto`
    )
    if (!res.ok) return null
    const data = await res.json()
    const sunrise = data.daily?.sunrise?.[0]
    const sunset = data.daily?.sunset?.[0]
    return {
      temp: data.current?.temperature_2m ?? 0,
      code: data.current?.weather_code ?? 0,
      timezone: data.timezone ?? '',
      currentTime: data.current?.time ?? new Date().toISOString(),
      sunrise: sunrise ?? data.current?.time ?? '',
      sunset: sunset ?? data.current?.time ?? ''
    }
  } catch {
    return null
  }
}

const ICON_SIZE = 40
const ICON_COLOR = '#ffffff'

function getWeatherIcon(isDay: boolean, code: number, temp: number): React.ReactNode {
  const isBitterCold = temp <= -10
  const iconProps = { size: ICON_SIZE, color: ICON_COLOR }
  const p = (
    Day: React.ComponentType<{ size?: number; color?: string }>,
    Night: React.ComponentType<{ size?: number; color?: string }>
  ) => (isDay ? <Day {...iconProps} /> : <Night {...iconProps} />)
  switch (code) {
    case 0:
      if (isBitterCold) return <WiSnowflakeCold size={ICON_SIZE} color={ICON_COLOR} />
      return p(WiDaySunny, WiNightClear)
    case 1:
      return isDay ? (
        <WiDayCloudyHigh size={ICON_SIZE} color={ICON_COLOR} />
      ) : (
        <WiNightAltPartlyCloudy size={ICON_SIZE} color={ICON_COLOR} />
      )
    case 2:
      return p(WiDayCloudy, WiNightCloudy)
    case 3:
      return isDay ? (
        <WiDayCloudy size={ICON_SIZE} color={ICON_COLOR} />
      ) : (
        <WiNightCloudyHigh size={ICON_SIZE} color={ICON_COLOR} />
      )
    case 45:
    case 48:
      return p(WiDayFog, WiNightFog)
    case 51:
    case 53:
    case 55:
      return temp <= 0 ? <WiSnow size={ICON_SIZE} color={ICON_COLOR} /> : p(WiDayRain, WiNightRain)
    case 56:
    case 57:
      return <WiSleet size={ICON_SIZE} color={ICON_COLOR} />
    case 61:
    case 63:
    case 65:
      return <WiRain size={ICON_SIZE} color={ICON_COLOR} />
    case 66:
    case 67:
      return <WiSleet size={ICON_SIZE} color={ICON_COLOR} />
    case 71:
      return isDay ? (
        isBitterCold ? (
          <WiDaySnowWind size={ICON_SIZE} color={ICON_COLOR} />
        ) : (
          <WiDaySnow size={ICON_SIZE} color={ICON_COLOR} />
        )
      ) : isBitterCold ? (
        <WiNightSnowWind size={ICON_SIZE} color={ICON_COLOR} />
      ) : (
        <WiNightSnow size={ICON_SIZE} color={ICON_COLOR} />
      )
    case 73:
    case 75:
      return isDay ? (
        <WiDaySnowWind size={ICON_SIZE} color={ICON_COLOR} />
      ) : (
        <WiNightSnowWind size={ICON_SIZE} color={ICON_COLOR} />
      )
    case 77:
      return isDay ? (
        <WiDaySnow size={ICON_SIZE} color={ICON_COLOR} />
      ) : (
        <WiNightAltSnow size={ICON_SIZE} color={ICON_COLOR} />
      )
    case 80:
    case 81:
    case 82:
      return <WiStormShowers size={ICON_SIZE} color={ICON_COLOR} />
    case 85:
      return p(WiDaySnow, WiNightSnow)
    case 86:
      return p(WiDaySnowWind, WiNightSnowWind)
    case 95:
      return p(WiDaySnowThunderstorm, WiNightSnowThunderstorm)
    case 96:
    case 99:
      return <WiThunderstorm size={ICON_SIZE} color={ICON_COLOR} />
    default:
      return p(WiDaySunny, WiNightClear)
  }
}

function formatTemp(n: number): string {
  return `${Math.round(n)}°`
}

export function WeatherWidget({
  city,
  useCurrentLocation,
  className,
  onClick
}: WeatherWidgetProps) {
  const [data, setData] = useState<WeatherData | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    const run = () => {
      setLoading(true)
      fetchWeather(city, useCurrentLocation).then((d) => {
        if (!cancelled) {
          setData(d ?? null)
          setLoading(false)
        }
      })
    }
    run()
    const interval = setInterval(run, 10 * 60 * 1000) // refresh every 10 min
    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [city, useCurrentLocation])

  const isDay = data
    ? isWithinInterval(new Date(data.currentTime), {
        start: new Date(data.sunrise),
        end: new Date(data.sunset)
      })
    : true

  const hasData = !!data
  const d = data
  const bgClass = !d
    ? 'bg-card'
    : d.temp <= -10
      ? 'bg-gradient-to-br from-slate-800 to-sky-900'
      : d.temp <= 0
        ? 'bg-gradient-to-br from-slate-700 to-sky-700'
        : isDay
          ? 'bg-gradient-to-br from-sky-500 to-cyan-700'
          : 'bg-gradient-to-br from-slate-900 to-indigo-900'

  const textClass = hasData ? 'text-blue-50' : 'text-muted-foreground'
  const labelClass = hasData ? 'text-blue-50/90' : 'text-muted-foreground'

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        EMPTY_WIDGET_SURFACE_CLASS,
        'flex-col items-start justify-center',
        bgClass,
        className
      )}
      aria-label={city ? `Weather in ${city}` : 'Weather'}
    >
      <div className={cn('mb-2 flex items-center gap-1.5 text-xs', labelClass)}>
        <span>{useCurrentLocation ? 'Current location' : city?.trim() || 'Current location'}</span>
      </div>
      {loading ? (
        <div className="flex w-full items-center gap-3">
          <div
            className={cn(
              'size-10 animate-pulse rounded-full',
              hasData ? 'bg-white/20' : 'bg-muted/50'
            )}
          />
          <div className="space-y-2">
            <div
              className={cn(
                'h-7 w-16 animate-pulse rounded-lg',
                hasData ? 'bg-white/20' : 'bg-muted/50'
              )}
            />
            <div
              className={cn(
                'h-3 w-20 animate-pulse rounded-full',
                hasData ? 'bg-white/15' : 'bg-muted/40'
              )}
            />
          </div>
        </div>
      ) : data ? (
        <div className="flex w-full flex-1 flex-row items-center justify-start gap-3">
          <div className="flex items-center justify-center">
            {getWeatherIcon(isDay, data.code, data.temp)}
          </div>
          <div className={cn('text-3xl font-semibold tabular-nums', textClass)}>
            {formatTemp(data.temp)}
          </div>
        </div>
      ) : (
        <div className={cn('text-sm', textClass)}>Unable to load</div>
      )}
    </button>
  )
}
